import { MapPin, HandCoins, MessageSquare, Loader2, Tag } from 'lucide-react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  COLLECTIONS,
  LISTING_ITEM_STATUSES,
  ROUTES,
} from '../../constants/index.ts';
import {
  useUploadImages,
  useAddData,
  useGetData,
  useUpdateFirestoreItem,
  useDeleteImages,
} from '../../hooks/index.ts';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useState, useEffect } from 'react';
import { type UploadProgress } from '../../hooks/useUploadImages.ts';
import {
  getSendInternalNotificationUrl,
  getFormattedLocations,
  getFormattedCategories,
} from '../../utils';
import { useAuth } from '../../providers/AuthProvider.tsx';
import {
  useLocation,
  useParams,
  useNavigate,
  generatePath,
} from 'react-router-dom';
import { AuthModal } from '../../components/AuthModal.tsx';
import { useToast } from '../../providers/ToastProvider.tsx';
import { LayoutWrapper } from '../../components/LayoutWrapper.tsx';
import { Select } from '../../components/Select.tsx';
import { ServiceFormSkeleton } from './ServiceFormSkeleton.tsx';
import { ImageUploader } from './ImageUploader.tsx';
import { SuccessSubmitModal } from '../../components/SuccessSubmitModal.tsx';

interface ServiceFormData {
  title: string;
  category: string;
  description: string;
  price: string;
  location: string;
  images: File[];
  phoneNumber: string;
  telegram: string;
}

const defaultValues = {
  phoneNumber: '',
  location: '',
  telegram: '',
  images: [],
  title: '',
  category: '',
  description: '',
  price: '',
};

const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
  <span>
    {children}
    <span className='text-red-500 ml-1'>*</span>
  </span>
);

const env = import.meta.env.VITE_ENV || 'dev';
const domain = import.meta.env.VITE_DOMAIN || 'dev.novoservice.net';

const sendNotificationToHelpEmail = async (data) => {
  try {
    await fetch(getSendInternalNotificationUrl(env), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error('Error:', error);
  }
};

export function AddEditService() {
  const { formatMessage } = useIntl();

  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [imagesChanged, setImagesChanged] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: serviceIdFromUrl } = useParams();
  const { state } = useLocation();
  const { data: userData, isPending: isPendingUserData } = useGetData(
    user?.uid,
    COLLECTIONS.users,
  );
  const { id } = useParams();
  const { data: serviceData, isLoading: isServiceLoading } = useGetData(id!);
  const { showToast } = useToast();
  const service = (state?.service || serviceData) ?? null;
  const filters = state?.filters ?? null;
  const isEditing = !!service;

  useEffect(() => {
    if (service) {
      setImagesChanged(false);
    }
  }, [service]);

  const form = useForm<ServiceFormData>({
    values: service ? { ...defaultValues, ...service } : defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    setValue,
    reset,
    control,
  } = form;

  const shouldEnableSubmit = isDirty || imagesChanged;

  const { uploadImages } = useUploadImages({
    setUploadProgress,
    userUuid: userData?.id,
  });
  const { mutate: deleteImages } = useDeleteImages();
  const { mutate: addItem } = useAddData<any>(
    COLLECTIONS.listings,
    ({ id }) => {
      reset();
      setShowSuccessModal(true);

      if (env === 'prod') {
        sendNotificationToHelpEmail({
          email: env,
          subject: 'New Service Added',
          message: `
              <p>A new service has been added. Click the link below to view it:</p>
              <p><a href="${domain}${ROUTES.services}/${id}">View Service</a></p>
            `,
          name: userData?.email,
        });
      }
    },
  );
  const { mutate: updateService } = useUpdateFirestoreItem();

  useEffect(() => {
    if (serviceIdFromUrl && !isPendingUserData && !userData) {
      navigate(ROUTES.addService);
    }
  }, [isPendingUserData, navigate, serviceIdFromUrl, userData]);

  useEffect(() => {
    if (user && userData && !isEditing && !isDirty) {
      setValue('phoneNumber', userData.phoneNumber || '');
      setValue('location', userData.location || '');
      setValue('telegram', userData.telegram || '');
    }
  }, [user, userData, setValue, isEditing, isDirty]);

  const handleUpload = async (uploadedFiles: File[]) => {
    if (!uploadedFiles.length) return;

    try {
      return await uploadImages(uploadedFiles);
    } catch (error) {
      console.log('error', error);

      showToast(
        formatMessage({
          id: 'addService.uploadError',
          defaultMessage: 'Ошибка при загрузке изображений',
        }),
        'error',
      );
    }
  };

  const onSubmit = async (data: ServiceFormData) => {
    if (!isEditing && !user) {
      setShowLoginModal(true);
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress({});

      let imageUrls: string[] = [];
      let imagesToDelete: string[] = [];

      const existingImageUrls = data.images.filter(
        (img) => typeof img === 'string',
      ) as string[];
      const filesToUpload = data.images.filter(
        (img) => img instanceof File,
      ) as File[];

      let uploadedImageUrls: string[] = [];
      if (filesToUpload.length > 0) {
        uploadedImageUrls = await handleUpload(filesToUpload);
      }

      if (service?.images) {
        imagesToDelete = service.images.filter(
          (img: string) => !existingImageUrls.includes(img),
        );
        imageUrls = [...existingImageUrls, ...uploadedImageUrls];
      } else {
        imageUrls = uploadedImageUrls;
      }

      const currentDate = new Date().toISOString();

      const baseData = {
        ...data,
        price: Number(data.price),
        images: imageUrls,
        providerUuid: user?.uid,
      };

      if (service) {
        const updatedFields: Partial<ServiceFormData> = {};
        Object.entries(data).forEach(([key, value]) => {
          const originalValue = service[key as keyof ServiceFormData];
          if (key === 'images') {
            if (
              imageUrls.length !== service.images.length ||
              !imageUrls.every((url, idx) => url === service.images[idx])
            ) {
              updatedFields.images = imageUrls;
            }
          } else if (JSON.stringify(value) !== JSON.stringify(originalValue)) {
            updatedFields[key as keyof ServiceFormData] = value;
          }
        });

        if (
          Object.keys(updatedFields).length > 0 ||
          imagesToDelete.length > 0
        ) {
          if (imagesToDelete.length > 0) {
            deleteImages(imagesToDelete);
          }

          if (Object.keys(updatedFields).length > 0) {
            updateService(
              {
                filters,
                id: service.id,
                newData: {
                  ...updatedFields,
                  updatedAt: currentDate,
                },
              },
              {
                onSuccess: () => {
                  showToast(
                    formatMessage({
                      id: 'editService.successToast',
                      defaultMessage: 'Услуга успешно обновлена!',
                    }),
                    'success',
                  );
                  navigate(
                    generatePath(ROUTES.serviceDetails, {
                      id: service.id,
                    }),
                  );
                },
                onError: () => {
                  showToast(
                    formatMessage({
                      id: 'editService.errorToast',
                      defaultMessage: 'Ошибка при обновлении услуги',
                    }),
                    'error',
                  );
                },
              },
            );
          }
        }
      } else {
        addItem({
          ...baseData,
          status: LISTING_ITEM_STATUSES.awaitingApproval,
          createdAt: currentDate,
        });
      }

      setIsUploading(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsUploading(false);
      showToast(
        formatMessage({
          id: 'service.submitError',
          defaultMessage: 'Ошибка при отправке формы',
        }),
        'error',
      );
    }
  };

  const handleTelegramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (!value.startsWith('@')) {
      value = '@' + value;
    }
    e.target.value = value;
  };

  const categories = getFormattedCategories(formatMessage);

  const locations = getFormattedLocations(formatMessage);

  return (
    <LayoutWrapper>
      {id && isServiceLoading && <ServiceFormSkeleton />}

      {!isServiceLoading && (
        <>
          <FormProvider {...form}>
            <div className='container mx-auto px-4 md:px-6'>
              <div className='max-w-2xl mx-auto'>
                <h1 className='text-3xl font-bold text-gray-900 mb-8'>
                  {service
                    ? formatMessage({
                        id: 'editService.title',
                        defaultMessage: 'Редактировать услугу',
                      })
                    : formatMessage({
                        id: 'addService.title',
                        defaultMessage: 'Разместить услугу',
                      })}
                </h1>

                <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                  {/* Title */}
                  <div>
                    <label
                      htmlFor='title'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      <RequiredLabel>
                        {formatMessage({
                          id: 'addService.titleLabel',
                          defaultMessage: 'Название услуги',
                        })}
                      </RequiredLabel>
                    </label>
                    <input
                      type='text'
                      id='title'
                      {...register('title', {
                        required: formatMessage({
                          id: 'addService.titleRequired',
                          defaultMessage: 'Название услуги обязательно',
                        }),
                        minLength: {
                          value: 5,
                          message: formatMessage({
                            id: 'addService.titleMinLength',
                            defaultMessage:
                              'Название должно содержать минимум 5 символов',
                          }),
                        },
                        maxLength: {
                          value: 100,
                          message: formatMessage({
                            id: 'addService.titleMaxLength',
                            defaultMessage:
                              'Название не должно превышать 100 символов',
                          }),
                        },
                      })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={formatMessage({
                        id: 'addService.titlePlaceholder',
                        defaultMessage: 'Например: Профессиональный массаж',
                      })}
                    />
                    {errors.title && (
                      <p className='mt-1 text-sm text-red-600'>
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label
                      htmlFor='category'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      <RequiredLabel>
                        {formatMessage({
                          id: 'addService.categoryLabel',
                          defaultMessage: 'Категория',
                        })}
                      </RequiredLabel>
                    </label>
                    <div className='relative w-full'>
                      <Tag className='absolute z-10 left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                      <Controller
                        name='category'
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            field={field}
                            options={categories}
                            placeholder={formatMessage({
                              id: 'addService.categoryRequired',
                              defaultMessage: 'Выберите категорию',
                            })}
                            errors={errors}
                            name={field.name}
                          />
                        )}
                      />
                      {errors.category && (
                        <p className='mt-1 text-sm text-red-600'>
                          {errors.category.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label
                      htmlFor='description'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      <RequiredLabel>
                        {formatMessage({
                          id: 'addService.descriptionLabel',
                          defaultMessage: 'Описание услуги',
                        })}
                      </RequiredLabel>
                    </label>
                    <textarea
                      id='description'
                      {...register('description', {
                        required: formatMessage({
                          id: 'addService.descriptionRequired',
                          defaultMessage: 'Описание услуги обязательно',
                        }),
                        minLength: {
                          value: 5,
                          message: formatMessage({
                            id: 'addService.descriptionMinLength',
                            defaultMessage:
                              'Описание должно содержать минимум 50 символов',
                          }),
                        },
                        maxLength: {
                          value: 2000,
                          message: formatMessage({
                            id: 'addService.descriptionMaxLength',
                            defaultMessage:
                              'Описание не должно превышать 2000 символов',
                          }),
                        },
                      })}
                      rows={5}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.description
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder={formatMessage({
                        id: 'addService.descriptionPlaceholder',
                        defaultMessage: 'Опишите подробно вашу услугу...',
                      })}
                    />
                    {errors.description && (
                      <p className='mt-1 text-sm text-red-600'>
                        {errors.description.message}
                      </p>
                    )}
                  </div>

                  {/* Price */}
                  <div>
                    <label
                      htmlFor='price'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      {formatMessage({
                        id: 'addService.priceLabel',
                        defaultMessage: 'Цена (RON)',
                      })}
                    </label>
                    <div className='relative'>
                      <input
                        type='text'
                        id='price'
                        {...register('price', {
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: formatMessage({
                              id: 'addService.pricePattern',
                              defaultMessage: 'Введите корректную цену',
                            }),
                          },
                        })}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.price ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={formatMessage({
                          id: 'addService.pricePlaceholder',
                          defaultMessage: 'Укажите стоимость',
                        })}
                      />
                      <HandCoins className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                    </div>
                    {errors.price && (
                      <p className='mt-1 text-sm text-red-600'>
                        {errors.price.message}
                      </p>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label
                      htmlFor='location'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      <RequiredLabel>
                        {formatMessage({
                          id: 'addService.locationLabel',
                          defaultMessage: 'Местоположение',
                        })}
                      </RequiredLabel>
                    </label>

                    <div className='relative w-full'>
                      <MapPin className='absolute z-10 left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                      <Controller
                        name='location'
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            field={field}
                            options={locations}
                            placeholder={formatMessage({
                              id: 'addService.locationRequired',
                              defaultMessage: 'Укажите местоположение',
                            })}
                            errors={errors}
                            name={field.name}
                          />
                        )}
                      />
                      {errors.location && (
                        <p className='mt-1 text-sm text-red-600'>
                          {errors.location.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor='phoneNumber'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      <RequiredLabel>
                        {formatMessage({
                          id: 'addService.phone',
                          defaultMessage: 'Телефон',
                        })}
                      </RequiredLabel>
                    </label>
                    <div className='relative'>
                      <Controller
                        name='phoneNumber'
                        control={control}
                        rules={{
                          required: formatMessage({
                            id: 'addService.phoneNumberIsRequired',
                            defaultMessage: 'Номер телефона обязателен',
                          }),
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <PhoneInput
                              country='ro'
                              value={value}
                              onChange={onChange}
                              inputProps={{
                                id: 'phoneNumber',
                                // required: true,
                                className: `w-full pl-12 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  error ? 'border-red-500' : 'border-gray-300'
                                }`,
                              }}
                              containerClass='phone-input'
                              buttonClass='phone-input-button'
                              dropdownClass='phone-input-dropdown'
                            />
                            {error && (
                              <p className='mt-1 text-sm text-red-600'>
                                {error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/* Telegram */}
                  <div>
                    <label
                      htmlFor='telegram'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Telegram
                    </label>
                    <div className='relative'>
                      <input
                        type='text'
                        id='telegram'
                        {...register('telegram', {
                          validate: (value) => {
                            if (!value || value.trim() === '') return true;
                            if (!/^@[a-zA-Z0-9_]{4,31}(?<!__)$/.test(value)) {
                              return formatMessage({
                                id: 'addService.telegramFormat',
                                defaultMessage:
                                  'Неверный формат username Telegram',
                              });
                            }
                            return true;
                          },
                        })}
                        onChange={handleTelegramChange}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.telegram ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder='@username'
                      />
                      <MessageSquare className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                    </div>
                    {errors.telegram && (
                      <p className='mt-1 text-sm text-red-600'>
                        {errors.telegram.message}
                      </p>
                    )}
                  </div>

                  <ImageUploader
                    uploadProgress={uploadProgress}
                    service={service}
                    setImagesChanged={setImagesChanged}
                    isUploading={isUploading}
                  />

                  {/* Submit Button */}
                  <div className='flex justify-end'>
                    {isEditing && (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            generatePath(state?.prevRoute || ROUTES.services, {
                              id: service.id,
                            }),
                          );
                        }}
                        className='mt-4 px-6 py-2 text-white rounded-lg focus:outline-none bg-blue-600 hover:bg-blue-700 mr-4'
                      >
                        <FormattedMessage
                          id='addService.successMessageDetailsButton.back'
                          defaultMessage='Назад'
                        />
                      </button>
                    )}

                    <button
                      type='submit'
                      disabled={isSubmitting || !shouldEnableSubmit}
                      className={`mt-4 px-6 py-2 text-white rounded-lg focus:outline-none transition-colors ${
                        isSubmitting || !shouldEnableSubmit
                          ? 'bg-gray-400 cursor-not-allowed opacity-50'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <div className='flex items-center'>
                          <Loader2 className='animate-spin mr-2 h-4 w-4' />
                          <span>
                            {formatMessage({
                              id: 'service.processing',
                              defaultMessage: 'Обработка...',
                            })}
                          </span>
                        </div>
                      ) : service ? (
                        formatMessage({
                          id: 'editService.submit',
                          defaultMessage: 'Редактировать',
                        })
                      ) : (
                        formatMessage({
                          id: 'addService.submit',
                          defaultMessage: 'Разместить',
                        })
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </FormProvider>
          {/* Login Modal */}
          <AuthModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
          />

          {/* Success Modal */}
          <SuccessSubmitModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
          />
        </>
      )}
    </LayoutWrapper>
  );
}
